/**
 * Splits a Content-Disposition header into its parameters, keeping semicolons inside quoted values.
 *
 * @param {string} header The header value.
 * @returns {Map<string, string>} Parameter values by lowercase name, with quotes removed.
 */
const parseParameters = (header: string): Map<string, string> => {
  const parameters = new Map<string, string>();
  for (const [, name, value] of header.matchAll(/;\s*([^\s=;]+)\s*=\s*("(?:[^"\\]|\\.)*"|[^;]*)/g)) {
    const unquoted = value.startsWith('"') ? value.slice(1, -1).replace(/\\(.)/g, '$1') : value.trim();
    parameters.set(name.toLowerCase(), unquoted);
  }
  return parameters;
};

/**
 * Decodes a UTF-8 `filename*` value (`charset'language'encoded`).
 *
 * @param {string} value The raw `filename*` value.
 * @returns {string | undefined} The filename, or undefined when it is malformed, empty or not UTF-8.
 */
const decodeExtendedValue = (value: string): string | undefined => {
  const match = /^([^']*)'[^']*'(.*)$/.exec(value);
  if (!match || match[1].toLowerCase() !== 'utf-8') {
    return undefined;
  }
  try {
    return decodeURIComponent(match[2]) || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Reads the download filename from a Content-Disposition header, preferring the UTF-8 `filename*` form.
 *
 * @param {string | null} [contentDisposition] The header value.
 * @returns {string | undefined} The filename, or undefined when the header has none.
 */
const extractFilename = (contentDisposition?: string | null): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }

  const parameters = parseParameters(contentDisposition);
  const encoded = parameters.get('filename*');
  return (encoded && decodeExtendedValue(encoded)) || parameters.get('filename') || undefined;
};

export { extractFilename };
